import { compareStrings } from "@core/utils/array";
import { components } from "@api/api";
import { useCallback } from "react";
import { useMetrics } from "@core/CommonDataContext";
import { KeywordTableColumn } from "@api/useGetKeywordBaseData";
import { getFormattedThousand } from "@datassential/platform-ui-lib";

export enum AffinityMetricColor {
    LoveIt = '#D3097C',
    LikeIt = '#F3289D',
    Neutral = '#FD70CB',
    Dislike = '#696969',
    HateIt = '#8C8C8C',
    NoOpinion = '#C1C1C1',
}

export enum ItemTileMetricColor {
    KnowIt = '#32C1E9',
    HadManyTimes = '#A5359A',
    LoveIt = '#E04598',
}

export enum ExperienceMetricColor {
    KnowIt = '#EA0F8C',
    HaveTriedIt = '#FF5EBB',
    HadManyTimes = '#FF7FD2',
}

export enum AffinityMetricColorGradient {
    LoveIt = 'linear-gradient(to top, #B60068, #EA0F8C)',
    LikeIt = 'linear-gradient(to top, #EB0087, #FF5EBB)',
    Neutral = 'linear-gradient(to top, #FC64C7, #FF7FD2)',
    Dislike = 'linear-gradient(to top, #5A5A5A, #808080)',
    HateIt = 'linear-gradient(to top, #7D7D7D, #AAAAAA)',
    NoOpinion = 'linear-gradient(to top, #AFAFAF, #C5C5C5)',
}

export enum Metric {
    NoData = 0,
    LoveIt = 1,
    LikeIt = 2,
    Neutral = 3,
    Dislike = 4,
    HateIt = 5,
    KnowIt = 6,
    HaveTriedIt = 7,
    HadManyTimes = 8,
    NoOpinion = 9,
    LoveLikeIt = 10,
    PolarityScore = 11,
}

export enum AffinityAmong {
    All = 0,
    KnowIt = 1,
    HaveTriedIt = 2,
}

export function compareMetrics(orderArray: Metric[], a?: Metric, b?: Metric): number {
    return compareStrings(
        orderArray,
        a ?? Metric.NoData,
        b ?? Metric.NoData,
    );
}

export const PROFILE_HEADER_METRICS_ORDER = [
    Metric.LoveLikeIt,
    Metric.KnowIt,
    Metric.HaveTriedIt,
]

export const PROFILE_EXPERIENCE_METRICS_ORDER = [
    Metric.KnowIt,
    Metric.HaveTriedIt,
    Metric.HadManyTimes,
];

export const TREND_METRICS_ORDER = [
    Metric.KnowIt,
    Metric.HaveTriedIt,
    Metric.HadManyTimes,
    Metric.LoveLikeIt,
    Metric.LoveIt,
    Metric.LikeIt,
    Metric.Neutral,
    Metric.Dislike,
    Metric.HateIt,
    Metric.NoOpinion,
];

export const PROFILE_CONSUMER_GROUPS_METRICS_ORDER = [
    Metric.KnowIt,
    Metric.HaveTriedIt,
    Metric.HadManyTimes,
    Metric.LoveIt,
    Metric.LoveLikeIt,
];

export const TREND_SUMMARY_METRICS_ORDER = [
    Metric.KnowIt,
    Metric.HaveTriedIt,
    Metric.LoveIt,
    Metric.LoveLikeIt,
];

export const CORRELATION_METRICS_ORDER = [
    Metric.KnowIt,
    Metric.HaveTriedIt,
    Metric.LoveLikeIt,
]

export const VISUALIZE_METRICS_ORDER = [
	Metric.KnowIt,
	Metric.HaveTriedIt,
	Metric.HadManyTimes,
	Metric.LoveLikeIt,
	Metric.LoveIt,
	Metric.LikeIt,
	Metric.HateIt,
];

export const HOME_TABLE_METRICS_ORDER = [
    Metric.KnowIt,
    Metric.HaveTriedIt,
    Metric.HadManyTimes,
    Metric.LoveLikeIt,
    Metric.LoveIt,
    Metric.LikeIt,
    Metric.Neutral,
    Metric.Dislike,
    Metric.HateIt,
];

export const HOME_TABLE_METRICS_POLARITY_ORDER = [
    ...HOME_TABLE_METRICS_ORDER,
    Metric.PolarityScore,
];

export const HOME_INDEX_METRICS_ORDER = [
    Metric.KnowIt,
    Metric.HaveTriedIt,
    Metric.LoveLikeIt,
];

export const HOME_GAIN_AFFINITY_ORDER = [
    AffinityAmong.All,
    AffinityAmong.KnowIt,
    AffinityAmong.HaveTriedIt,
];

export const HOME_PEOPLE_DISPLAY_METRICS_ORDER = [
    Metric.KnowIt,
    Metric.HaveTriedIt,
    Metric.HadManyTimes,
    Metric.LoveLikeIt,
    Metric.LoveIt,
    Metric.LikeIt,
    Metric.Neutral,
    Metric.Dislike,
    Metric.HateIt,
];

export const HOME_SORT_BY_METRICS_ORDER = [Metric.KnowIt, Metric.HadManyTimes, Metric.LoveIt]

export const KEYWORD_TILE_METRICS_ORDER = [Metric.KnowIt, Metric.HadManyTimes, Metric.LoveIt]

export function getMetricColor(metric?: Metric): string {
    switch (metric) {
        case Metric.LoveLikeIt:
            return AffinityMetricColor.LoveIt;
        case Metric.LoveIt:
            return AffinityMetricColor.LoveIt;
        case Metric.LikeIt:
            return AffinityMetricColor.LikeIt;
        case Metric.Neutral:
            return AffinityMetricColor.Neutral;
        case Metric.Dislike:
            return AffinityMetricColor.Dislike;
        case Metric.HateIt:
            return AffinityMetricColor.HateIt;
        case Metric.KnowIt:
            return ExperienceMetricColor.KnowIt;
        case Metric.HaveTriedIt:
            return ExperienceMetricColor.HaveTriedIt;
        case Metric.HadManyTimes:
            return ExperienceMetricColor.HadManyTimes;
        case Metric.NoOpinion:
            return AffinityMetricColor.NoOpinion;
        default:
            return '';
    }
}

export function getMetricBgColor(metric?: Metric): string {
    switch (metric) {
        case Metric.LoveLikeIt:
            return AffinityMetricColorGradient.LoveIt;
        case Metric.LoveIt:
            return AffinityMetricColorGradient.LoveIt;
        case Metric.LikeIt:
            return AffinityMetricColorGradient.LikeIt;
        case Metric.Neutral:
            return AffinityMetricColorGradient.Neutral;
        case Metric.Dislike:
            return AffinityMetricColorGradient.Dislike;
        case Metric.HateIt:
            return AffinityMetricColorGradient.HateIt;
        case Metric.NoOpinion:
            return AffinityMetricColorGradient.NoOpinion;
        default:
            return '';
    }
}

export function getItemTileMetricColor(metric?: Metric): string {
    switch (metric) {
        case Metric.KnowIt:
            return ItemTileMetricColor.KnowIt;
        case Metric.HadManyTimes:
            return ItemTileMetricColor.HadManyTimes;
        case Metric.LoveIt:
            return ItemTileMetricColor.LoveIt;
        default:
            return '';
    }
}

export function cleanUpMetricName(name: string, keepIt = false): string {
    let cleanName = name.replace(/ or /ig, ' / ')
    if (!keepIt) {
        cleanName = cleanName.replace(/ it/ig, '');
    }
	return cleanName;
}

export type MetricBase = {
	id: Metric;
	name: string;
	value: number | null;
}

export type MetricForTable = KeywordTableColumn<Metric>;

type MetricCommonTypeForTable = components["schemas"]["FlavorTracker.Entities.DTO.Common.MetricDataWithOriginalAndNormBase"];

export function useMapMetricsForTable(orderList?: Metric[]) {
    const metrics = useMetrics(orderList);

    return useCallback(
        (data: MetricCommonTypeForTable[]): MetricForTable[] => {
            return metrics.map(m => {
                const metricData = data.find(mm => mm.metric === m.id);
                const isPolarityMetric = m.id === Metric.PolarityScore;

                if (isPolarityMetric) {
                    const value = metricData?.norm ?? null;

                    return {
                        id: m.id ?? Metric.NoData,
                        name: m.name ?? '',
                        value,
                        tableValue: value !== null ? getFormattedThousand(value) : '-',
                        copyTableValue: value !== null ? `${value}` : '-',
                        sortValue: value ?? null,
                        norm: value,
                    };
                }

                const value = metricData?.raw ?? null;

                return {
                    id: m.id ?? Metric.NoData,
                    name: m.name ?? '',
                    value,
                    tableValue: value !== null ? `${value}%` : '-',
                    copyTableValue: value !== null ? `${value}%` : '-',
                    sortValue: metricData?.originalRaw ?? null,
                    norm: metricData?.norm ?? null,
                };
            })
        },
        [metrics],
    );
}

type MetricCommonTypeForIndex = components["schemas"]["FlavorTracker.Entities.DTO.Common.MetricDataIndex"];

export function useMapMetricsForIndex(orderList?: Metric[]) {
    const metrics = useMetrics(orderList);

    return useCallback(
        (data: MetricCommonTypeForIndex[]): MetricForTable[] => {
            return metrics.map(m => {
                const metricData = data.find(mm => mm.metric === m.id);

                const value = metricData?.index ?? null;

                return {
                    id: m.id ?? Metric.NoData,
                    name: m.name ?? '',
                    value: value,
                    tableValue: value !== null ? `${value}` : '-',
                    copyTableValue: value !== null ? `${value}` : '-',
                    sortValue: metricData?.index ?? null,
                };
            })
        },
        [metrics],
    );
}